import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import { ProductCardDefaultViewOption, ProductCardTextAlignmentOption } from 'types/graphql';

import useErnie from 'shared/hooks/use-ernie';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { ProductCardsFormProps } from './product-card.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  defaultViewLarge: ProductCardDefaultViewOption;
  defaultViewSmall: ProductCardDefaultViewOption;
  fullWidthImages: boolean;
  mobileShowCollection: boolean;
  mobileShowDiscountNames: boolean;
  mobileShowEffects: boolean;
  mobileShowPotencyDetails: boolean;
  mobileShowStaffPick: boolean;
  mobileShowStrainType: boolean;
  showProductWeight: boolean;
  textAlignment: ProductCardTextAlignmentOption;
};

type Input = {
  productCards: {
    defaultViewLarge: ProductCardDefaultViewOption;
    defaultViewSmall: ProductCardDefaultViewOption;
    fullWidthImages: boolean;
    mobileAppDisplayOptions: {
      showCollection: boolean;
      showDiscountNames: boolean;
      showEffects: boolean;
      showPotencyDetails: boolean;
      showStaffPick: boolean;
      showStrainType: boolean;
    };
    showProductWeight: boolean;
    textAlignment: ProductCardTextAlignmentOption;
  };
};

type UseProductCardsFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: (this: void) => void;
  isDirty: boolean;
  previewData: PreviewData;
  applyAllInput: Input;
  afterApplyAll: () => void;
};

function useMergeDataWithFormValues(data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const {
    defaultViewLarge,
    defaultViewSmall,
    fullWidthImages,
    mobileShowCollection,
    mobileShowDiscountNames,
    mobileShowEffects,
    mobileShowPotencyDetails,
    mobileShowStaffPick,
    mobileShowStrainType,
    showProductWeight,
    textAlignment,
  } = formValues;

  return {
    ...data,
    productCards: {
      defaultViewLarge,
      defaultViewSmall,
      fullWidthImages,
      mobileAppDisplayOptions: {
        showCollection: mobileShowCollection,
        showDiscountNames: mobileShowDiscountNames,
        showEffects: mobileShowEffects,
        showPotencyDetails: mobileShowPotencyDetails,
        showProductWeight,
        showStaffPick: mobileShowStaffPick,
        showStrainType: mobileShowStrainType,
      },
      showProductWeight,
      textAlignment,
    },
  };
}

function formatInput(values: FormValues): Input {
  return {
    productCards: {
      defaultViewLarge: values.defaultViewLarge,
      defaultViewSmall: values.defaultViewSmall,
      fullWidthImages: values.fullWidthImages,
      mobileAppDisplayOptions: {
        showCollection: values.mobileShowCollection,
        showDiscountNames: values.mobileShowDiscountNames,
        showEffects: values.mobileShowEffects,
        showPotencyDetails: values.mobileShowPotencyDetails,
        showStaffPick: values.mobileShowStaffPick,
        showStrainType: values.mobileShowStrainType,
      },
      showProductWeight: values.showProductWeight,
      textAlignment: values.textAlignment,
    },
  };
}

export function useProductCardsForm({ data }: ProductCardsFormProps): UseProductCardsFormReturn {
  const {
    productCards: {
      defaultViewLarge,
      defaultViewSmall,
      fullWidthImages,
      mobileAppDisplayOptions,
      showProductWeight,
      textAlignment,
    },
  } = data;

  const defaultValues = {
    defaultViewLarge,
    defaultViewSmall,
    fullWidthImages,
    mobileShowCollection: mobileAppDisplayOptions.showCollection ?? false,
    mobileShowDiscountNames: mobileAppDisplayOptions.showDiscountNames ?? false,
    mobileShowEffects: mobileAppDisplayOptions.showEffects ?? false,
    mobileShowPotencyDetails: mobileAppDisplayOptions.showPotencyDetails ?? false,
    mobileShowStaffPick: mobileAppDisplayOptions.showStaffPick ?? false,
    mobileShowStrainType: mobileAppDisplayOptions.showStrainType ?? false,
    showProductWeight,
    textAlignment,
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
  } = useForm<FormValues>({ defaultValues });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();

  const updateData = useUpdateWebCustomizationSettingsV2();

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    const input = formatInput(submittedValues);

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your product card settings have been updated', 'success');
      reset({ ...submittedValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  const formValues = watch();

  const previewData = useMergeDataWithFormValues(data, formValues);

  const applyAllInput = formatInput(formValues);
  const afterApplyAll = (): void => {
    reset({ ...formValues });
  };

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  };
}
